/* eslint-disable no-underscore-dangle */
//@ts-nocheck
import currency from "currency.js";
import { cssColor } from "../../utils/themeColor";
import {
  BACKGROUND_COLORS,
  TEXT_COLORS,
} from "../../constants/design/colors";
import { FONT_SIZE } from "../../constants/design/fontSize";
import { SPACING } from "../../constants/design/spacing";
import { drawRoundRect } from "../../utils/canvas";
import { getRange } from "../../utils/math";

// Direction colors for canvas/chart.js (can't parse var()-based COLORS.up/down).
export const upColor = (a?: number) => cssColor("--color-up", "22 199 132", a);
export const downColor = (a?: number) => cssColor("--color-down", "234 57 67", a);

export const drawLabelImpl = (
  ctx: CanvasRenderingContext2D,
  label: string,
  value: string,
  valueColor: string,
  x: number,
  y: number,
  hasRectBg?: boolean
) => {
  ctx.save();
  ctx.font = `${FONT_SIZE.one} BaseFont`;
  const valueWithSpace = ` ${value}`;
  const labelLength = ctx.measureText(label).width;
  const valueLength = ctx.measureText(valueWithSpace).width;

  const canvasWidth = ctx.canvas.clientWidth;
  const minLabelX = 28;
  const maxLabelX = canvasWidth - labelLength - valueLength - 24;
  let labelX = x - (labelLength + valueLength) / 2;
  if (labelX < minLabelX) {
    labelX = minLabelX;
  } else if (labelX > maxLabelX) {
    labelX = maxLabelX;
  }
  const valueX = labelX + labelLength;

  const rectHeight = 24;
  ctx.fillStyle = BACKGROUND_COLORS.eight;
  ctx.beginPath();

  // DRAW RECT W/ ROUNDED CORNER
  if (hasRectBg) {
    const rectPadding = SPACING.two;
    const rectWidth = labelLength + valueLength + rectPadding * 2;
    drawRoundRect(
      ctx,
      labelX - rectPadding,
      y - rectHeight / 2,
      rectWidth,
      rectHeight,
      {
        upperLeft: 8,
        upperRight: 8,
        lowerLeft: 8,
        lowerRight: 8,
      },
      true,
      false
    );
  }

  // Draw text
  const labelY = y + rectHeight * 0.5 * 0.5 * 0.5;
  ctx.fillStyle = TEXT_COLORS.three;
  ctx.textAlign = "left";
  ctx.fillText(label, labelX, labelY);

  ctx.fillStyle = valueColor;
  ctx.fillText(valueWithSpace, valueX, labelY);

  ctx.restore();
};

export const findNeighbourPointImpl = (nums: number[], point: number) => {
  let closestSmallerNum: number | undefined;
  let closestBiggerNum: number | undefined;

  for (let i = 0; i < nums.length; i += 1) {
    const currNum = nums[i];

    // Find closest bigger number
    if (
      currNum >= point &&
      (closestSmallerNum === undefined || currNum < closestSmallerNum)
    ) {
      if (closestSmallerNum === undefined || currNum < closestSmallerNum) {
        closestSmallerNum = currNum;
      }
    }

    // Find closest smaller number
    if (
      currNum <= point &&
      (closestBiggerNum === undefined || currNum > closestBiggerNum)
    ) {
      closestBiggerNum = currNum;
    }
  }

  // return 2 closest point
  return [closestSmallerNum!, closestBiggerNum!];
};

export const computePriceRange = (
  breakeven: number,
  premium: number,
  strike: number
) =>
  premium > 0
    ? getRange(
        Math.round(breakeven * (1 - (premium * 5) / strike)),
        Math.round(breakeven * (1 + (premium * 5) / strike)),
        breakeven *
          (premium > 0
            ? premium / strike / 10
            : breakeven * (1 + (premium * 5) / strike))
      )
    : getRange(
        Math.round(breakeven * 0.9),
        Math.round(breakeven * 1.1),
        breakeven * 0.02
      );

export const drawPricePointImpl = (
  chart: any,
  pricePoint: number,
  drawIndex: number,
  y: number,
  {
    calculateProfit,
    drawLabel,
    t,
    asset,
  }: {
    calculateProfit: (p: number) => number;
    drawLabel: (
      ctx: CanvasRenderingContext2D,
      label: string,
      value: string,
      valueColor: string,
      x: number,
      y: number,
      hasRectBg?: boolean
    ) => void;
    t: any;
    asset: any;
  }
) => {
  // eslint-disable-next-line prefer-destructuring
  const ctx: CanvasRenderingContext2D = chart.chart.ctx;

  // Get breakeven dataset because of stability over every point
  const datasetIndex = chart.chart.data.datasets.findIndex(
    (dataset: any) => dataset.label === "breakeven"
  );
  const meta = chart.chart.getDatasetMeta(datasetIndex);
  const topY = chart.chart.scales["y-axis-0"].top;
  /**
   * Draw price point
   */
  const priceElement = meta.data[drawIndex];
  const priceX = priceElement._view.x;
  const profit = calculateProfit(pricePoint);

  ctx.save();
  ctx.beginPath();
  ctx.moveTo(priceX, topY);
  ctx.lineTo(priceX, y);
  ctx.lineWidth = 1;

  ctx.strokeStyle = TEXT_COLORS.one;
  ctx.stroke();
  ctx.restore();

  // Draw price text
  drawLabel(
    ctx,
    `${
      // eslint-disable-next-line no-nested-ternary
      drawIndex === 0
        ? "<<< "
        : drawIndex === meta.data.length - 1
        ? ">>> "
        : ""
    }${t("asset_spot", { asset })}`,
    currency(pricePoint).format(),
    profit >= 0 ? upColor() : downColor(),
    priceX,
    topY,
    true
  );
};
